import { App as AntdApp, Button, Space } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePortalBuildFreshness } from '../../hooks/usePortalBuildFreshness';
import { auth } from '../../providers/authProvider';
import { reloadPortal } from '../../providers/portalBuild';
import { appendPortalUpdatePromptShownUsage } from '../../providers/usageEvents';

/**
 * E30 · `ReloadPortal · banner` — the only thing this epic puts on screen.
 *
 * ─── WHY IT NEVER RELOADS BY ITSELF ─────────────────────────────────────────────────────────────
 * v1 is the banner ONLY (founder 2026-09-13: "the banner only for v1"), which is what makes the
 * dirty-form guarantee hold BY CONSTRUCTION rather than by a guard that could be wrong: nothing
 * reloads without the person's click, so a half-written progress note cannot be lost. Auto-reload
 * on a route change is drawn as the `Planned` slice `E30 · AutoReloadOnRouteChange`, and the
 * dirty-form guard is its second GWT — not this one's.
 *
 * ─── ONE CARD, NOT A STACK ──────────────────────────────────────────────────────────────────────
 * `key: portal-update` means a second check UPDATES the open card instead of stacking a second one,
 * and `duration: 0` means it never auto-closes. It is dismissible (Later / ×) and reappears at the
 * next check that still finds the tab stale — which falls out of `checkedAt` changing rather than
 * from any dismissal bookkeeping.
 *
 * ─── MOUNTED ABOVE `Refine`, ON PURPOSE ─────────────────────────────────────────────────────────
 * It renders nothing, so it can sit directly under the antd `App` shell in `App.tsx` and watch for
 * the whole tab regardless of which route is open. That is also why the copy comes from
 * `react-i18next` rather than Refine's `useTranslate` — there is no Refine context this high up.
 */
export const PORTAL_UPDATE_NOTIFICATION_KEY = 'portal-update';

export const PortalUpdateBanner = (): null => {
  const { t } = useTranslation();
  const { notification } = AntdApp.useApp();
  const { bakedBuildId, deployedBuildId, stale, checkedAt } =
    usePortalBuildFreshness();

  useEffect(() => {
    if (!stale || !deployedBuildId) {
      return;
    }

    notification.open({
      key: PORTAL_UPDATE_NOTIFICATION_KEY,
      role: 'status',
      duration: 0,
      props: { 'data-testid': 'cc-portal-update-banner' },
      message: t('portalUpdate.available.title'),
      description: t('portalUpdate.available.description', {
        buildId: deployedBuildId,
      }),
      btn: (
        <Space>
          <Button
            size="small"
            data-testid="cc-portal-update-later"
            onClick={() =>
              notification.destroy(PORTAL_UPDATE_NOTIFICATION_KEY)
            }
          >
            {t('portalUpdate.available.dismiss')}
          </Button>
          <Button
            type="primary"
            size="small"
            data-testid="cc-portal-update-reload"
            onClick={reloadPortal}
          >
            {t('portalUpdate.available.reload')}
          </Button>
        </Space>
      ),
    });

    // The emit rides the notification's OWN open, so it is recorded exactly when a person could
    // have seen it — never on a check that found nothing. Telemetry never fails the surface it
    // rides on (`appendPortalUsageEvent` reports to Sentry and swallows).
    void appendPortalUpdatePromptShownUsage(bakedBuildId, !!auth.currentUser);
    // `checkedAt` is in the deps on purpose: a later check that STILL finds the tab stale re-opens
    // a card the person dismissed, which is the modelled behaviour.
  }, [stale, deployedBuildId, bakedBuildId, checkedAt, notification, t]);

  return null;
};
